import { type VariantProps, cva } from 'class-variance-authority';
import { AlertCircle, EyeIcon, EyeOffIcon } from 'lucide-react';
import React, { useState } from 'react';
import { RefCallBack } from 'react-hook-form';

import { Button } from '@core/components/Button';
import { Label } from '@core/components/Label';
import { cn } from '@core/utils/styles';

const inputVariants = cva(
  [
    // Input
    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',

    // Layout
    'w-full',

    // Background
    'bg-back-input-light',
    'dark:bg-back-input-dark',

    // Typography
    'text-txt-light',
    'dark:text-txt-dark',

    // Border
    'rounded-theme-min border',

    // Shadow
    'shadow-xs',

    // Interaction
    'focus:outline-hidden focus:ring',

    // Disabled
    'disabled:opacity-75',
  ],
  {
    variants: {
      variant: {
        default: [
          // Border
          'border-input-stroke-light/50',
          'dark:border-input-stroke-dark/50',

          // Ring, Outline
          'focus:border-primary-light',
          'focus:ring-primary-light',
          'dark:focus:ring-primary-light',

          // Interaction
          'active:border-input-stroke-light',
          'dark:active:border-input-stroke-dark',
          'group-hover:border-input-stroke-light',
          'dark:group-hover:border-input-stroke-dark',
        ],
        error: [
          // Typography
          'dark:text-txt-dark',

          // Border
          'border-red-600',
          'dark:border-red-400',

          // Ring, Outline
          'focus:border-red-600',
          'focus:ring-red-600',
          'dark:focus:border-red-400',
          'dark:focus:ring-red-400',
        ],
      },
      size: {
        sm: 'px-2 py-1 text-sm',
        default: 'px-3 py-1.5 sm:min-h-9',
        lg: 'px-4 py-3 text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  containerClassName?: string;
  label?: string;
  initialValue?: string | number;
  error?: string;
  helperText?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  endAction?: React.ReactNode;
  onClickEndAction?: (value?: string | number) => void;
}

const getEndAction = (type: string | undefined, showPassword: boolean) => {
  if (type === 'password') {
    return showPassword ? (
      <EyeOffIcon className="text-icon-light dark:text-icon-dark h-4 w-4" />
    ) : (
      <EyeIcon className="text-icon-light dark:text-icon-dark h-4 w-4" />
    );
  }
  return null;
};

export const TextInput = ({
  ref,
  label,
  initialValue,
  error,
  helperText,
  className,
  containerClassName,
  size,
  startIcon,
  endIcon,
  endAction: endActionProp,
  onClickEndAction: onClickEndActionProp,
  id,
  name,
  type: typeProp,
  ...props
}: TextInputProps & {
  ref?: React.RefObject<HTMLInputElement | null> | RefCallBack;
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id || `input-${name}`;

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const endAction = endActionProp ? endActionProp : getEndAction(typeProp, showPassword);

  const onClickEndAction = onClickEndActionProp
    ? onClickEndActionProp
    : typeProp === 'password'
      ? toggleShowPassword
      : undefined;

  const type = typeProp === 'password' ? (showPassword ? 'text' : 'password') : typeProp;

  return (
    <div className={cn('group col-span-full flex flex-col gap-1.5', containerClassName)}>
      {label && <Label htmlFor={inputId}>{label}</Label>}

      {helperText && !error && (
        <p
          id={`${inputId}-helper`}
          className="text-txt-light-muted dark:text-txt-dark-muted text-sm"
        >
          {helperText}
        </p>
      )}

      {error && (
        <div className="flex items-center gap-1">
          <AlertCircle className="h-4 w-4 text-red-700 dark:text-red-300" />
          <p
            id={`${inputId}-error`}
            className="text-sm text-red-700 dark:text-red-300"
            role="alert"
          >
            {error}
          </p>
        </div>
      )}

      <div className="relative flex">
        {startIcon && (
          <div className="text-icon-light dark:text-icon-dark pointer-events-none absolute top-1/2 left-0 flex -translate-y-1/2 items-center pl-3">
            {startIcon}
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          name={name}
          defaultValue={initialValue}
          className={cn(
            inputVariants({
              variant: error ? 'error' : 'default',
              size,
            }),
            `${startIcon ? 'pl-10' : ''} ${endIcon ? 'pr-10' : ''}`,
            endAction && 'pr-10',
            className
          )}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          type={type}
          aria-required={props.required}
          {...props}
        />

        {endIcon && (
          <div className="text-icon-light dark:text-icon-dark pointer-events-none absolute top-1/2 right-0 flex -translate-y-1/2 items-center pr-3">
            {endIcon}
          </div>
        )}

        {endAction && (
          <div className="absolute inset-y-0 right-0.75 flex items-center justify-center">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="rounded-theme-min aspect-square justify-center"
              onClick={() =>
                onClickEndAction?.(
                  (document.getElementById(inputId) as HTMLInputElement)?.value ?? ''
                )
              }
              aria-label={`${inputId} action`}
              name={`${inputId}-action`}
            >
              {endAction}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
TextInput.displayName = 'TextInput';
